import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";
import { novaInutilizacaoBreadcrumbItems, tiposDocumento } from "data/inutilizacao";
import { transformErrors } from "helpers/utils";
import { Card, Col, Form, Row, Alert } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useEmpresas } from "providers/EmpresaProvider";
import { useEffect, useState } from "react";

type InutilizarFormData = {
    empresaId: string;
    tipoDocumento: number;
    serie: number;
    inicio: number;
    fim: number;
    justificativa: string;
};

const NovaInutilizacao = () => {
    const { selectedIds, empresas } = useEmpresas();
    const empresasDisponiveis = empresas.filter(e => selectedIds.includes(e.id));
    const [generalError, setGeneralError] = useState<string | null>(null);
    const { showToast } = useToast();
    const navigate = useNavigate();

    const form = useForm<InutilizarFormData>({
        defaultValues: {
            empresaId: "",
            tipoDocumento: 55, // NFe como padrão
            serie: 1,
            inicio: 1,
            fim: 1,
            justificativa: "",
        },
    });

    // Preenche automaticamente a empresa quando há apenas uma disponível
    useEffect(() => {
        if (empresasDisponiveis.length === 1) {
            form.setValue("empresaId", empresasDisponiveis[0].id);
        } else if (empresasDisponiveis.length === 0) {
            form.setValue("empresaId", "");
        }
    }, [empresasDisponiveis, form]);

    const mutation = useMutation<any, any, InutilizarFormData>({
        mutationFn: (data: InutilizarFormData) => {
            return axios.post("/api/inutilizacao", data);
        },
        onSuccess: (response) => {
            const responseData = response.data;

            // Verifica se a resposta indica falha mesmo com status 200
            if (responseData?.isSuccess === false) {
                const errorMessage = responseData?.message || 'Erro ao processar inutilização';
                setGeneralError(errorMessage);
                return; // Não redireciona
            }

            // Sucesso real - mostra toast e redireciona
            setGeneralError(null);
            showToast('Inutilização processada com sucesso!', "success");

            // Redireciona após um breve delay para mostrar a mensagem
            setTimeout(() => {
                navigate('/operacional/inutilizacao');
            }, 1500);
        },
        onError: (error) => {
            setGeneralError(null); // Limpa erro anterior

            const responseData = error.response?.data;

            // Verifica se há erros de validação específicos de campos
            if (responseData?.errors) {
                const formattedErrors = transformErrors(responseData.errors);
                for (const [field, error] of Object.entries(formattedErrors)) {
                    form.setError(field as any, error as any);
                }
            }
            // Verifica se a resposta tem isSuccess: false (erro de negócio)
            else if (responseData?.isSuccess === false) {
                const errorMessage = responseData?.message || 'Erro ao processar inutilização';
                setGeneralError(errorMessage);
            }
            // Verifica se há erro geral de negócio (como conflito de numeração)
            else if (responseData?.message || typeof responseData === 'string') {
                const errorMessage = responseData?.message || responseData || 'Erro ao processar inutilização';
                setGeneralError(errorMessage);
            }
            // Fallback para outros tipos de erro
            else {
                setGeneralError('Erro inesperado ao processar inutilização. Tente novamente.');
            }
        }
    });

    const onSubmit: SubmitHandler<InutilizarFormData> = (data) => {
        if (!data.empresaId || data.empresaId === "") {
            return;
        }
        setGeneralError(null); // Limpa erro ao submeter
        mutation.mutate(data);
    };

    return (
        <div>
            <FormProvider {...form}>
                <PageBreadcrumb items={novaInutilizacaoBreadcrumbItems} />
                <Form
                    noValidate
                    className="mb-9"
                    onSubmit={form.handleSubmit(onSubmit)}
                >
                    <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                        <div>
                            <h2 className="mb-2">Nova Inutilização</h2>
                            <h5 className="text-body-tertiary fw-semibold">
                                Preencha as informações da inutilização
                            </h5>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="secondary"
                                as={Link}
                                to="/operacional/inutilizacao"
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                loading={mutation.isPending}
                                disabled={mutation.isPending || empresasDisponiveis.length === 0}
                                loadingPosition="start"
                            >
                                Inutilizar
                            </Button>
                        </div>
                    </div>



                    <Row className="g-5">
                        <Col xs={12} xl={8}>
                            {empresasDisponiveis.length === 0 && (
                                <Alert variant="outline-warning" className="mb-3">
                                    <strong>⚠️ Atenção:</strong> Selecione uma empresa no seletor de empresas para criar uma inutilização.
                                </Alert>
                            )}

                            {generalError && (
                                <Alert variant="outline-danger" className="mb-3" dismissible onClose={() => setGeneralError(null)}>
                                    <strong>❌ Erro:</strong> {generalError}
                                </Alert>
                            )}

                            <Card className="mb-3">
                                <Card.Body>
                                    <h4 className="mb-3">Dados da Inutilização</h4>
                                    <Row className="g-3">
                                        <Form.Group as={Col} xs={12} md={6} controlId="empresaId">
                                            <Form.Label>Empresa *</Form.Label>
                                            <Form.Select
                                                {...form.register("empresaId", {
                                                    required: "Selecione uma empresa",
                                                })}
                                                isInvalid={!!form.formState.errors.empresaId}
                                                disabled={empresasDisponiveis.length === 0}
                                            >
                                                <option value={0}>Selecione uma empresa</option>
                                                {empresasDisponiveis.map((empresa) => (
                                                    <option key={empresa.id} value={empresa.id}>
                                                        {empresa.nomeFantasia}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.empresaId?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group as={Col} xs={12} md={6} controlId="tipoDocumento">
                                            <Form.Label>Tipo de Documento *</Form.Label>
                                            <Form.Select
                                                {...form.register("tipoDocumento", {
                                                    required: "Selecione o tipo de documento",
                                                    valueAsNumber: true,
                                                })}
                                                isInvalid={!!form.formState.errors.tipoDocumento}
                                                disabled={empresasDisponiveis.length === 0}
                                            >
                                                {tiposDocumento.map((tipo) => (
                                                    <option key={tipo.id} value={tipo.id}>
                                                        {tipo.descricao}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.tipoDocumento?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group as={Col} xs={12} md={4} controlId="serie">
                                            <Form.Label>Série *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                {...form.register("serie", {
                                                    required: "Informe a série",
                                                    valueAsNumber: true,
                                                    min: {
                                                        value: 1,
                                                        message: "Série deve ser maior que 0"
                                                    }
                                                })}
                                                isInvalid={!!form.formState.errors.serie}
                                                disabled={empresasDisponiveis.length === 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.serie?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group as={Col} xs={12} md={4} controlId="inicio">
                                            <Form.Label>Número Inicial *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                {...form.register("inicio", {
                                                    required: "Informe o número inicial",
                                                    valueAsNumber: true,
                                                    min: {
                                                        value: 1,
                                                        message: "Número deve ser maior que 0"
                                                    }
                                                })}
                                                isInvalid={!!form.formState.errors.inicio}
                                                disabled={empresasDisponiveis.length === 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.inicio?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group as={Col} xs={12} md={4} controlId="fim">
                                            <Form.Label>Número Final *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                {...form.register("fim", {
                                                    required: "Informe o número final",
                                                    valueAsNumber: true,
                                                    min: {
                                                        value: 1,
                                                        message: "Número deve ser maior que 0"
                                                    },
                                                    validate: (value) => {
                                                        const inicio = form.getValues("inicio");
                                                        if (value < inicio) {
                                                            return "Número final deve ser maior ou igual ao inicial";
                                                        }
                                                        return true;
                                                    }
                                                })}
                                                isInvalid={!!form.formState.errors.fim}
                                                disabled={empresasDisponiveis.length === 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.fim?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group as={Col} xs={12} controlId="justificativa">
                                            <Form.Label>Justificativa *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                placeholder="Informe o motivo da inutilização..."
                                                {...form.register("justificativa", {
                                                    required: "A justificativa é obrigatória",
                                                    minLength: {
                                                        value: 15,
                                                        message: "A justificativa deve ter pelo menos 15 caracteres"
                                                    }
                                                })}
                                                isInvalid={!!form.formState.errors.justificativa}
                                                disabled={empresasDisponiveis.length === 0}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {form.formState.errors.justificativa?.message}
                                            </Form.Control.Feedback>
                                            <Form.Text className="text-muted">
                                                Mínimo de 15 caracteres
                                            </Form.Text>
                                        </Form.Group>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} xl={4}>
                            <Card>
                                <Card.Body>
                                    <h5 className="mb-3">Informações Importantes</h5>
                                    <div className="text-muted small">
                                        <p className="mb-2">
                                            <strong>⚠️ Atenção:</strong> A inutilização é um processo irreversível.
                                        </p>
                                        <p className="mb-2">
                                            <strong>📋 Justificativa:</strong> Deve conter o motivo detalhado da inutilização.
                                        </p>
                                        <p className="mb-2">
                                            <strong>🔢 Numeração:</strong> Certifique-se de que os números não foram utilizados.
                                        </p>
                                        <p className="mb-0">
                                            <strong>📄 Comprovante:</strong> Será gerado automaticamente após a inutilização.
                                        </p>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
        </div>
    );
};

export default NovaInutilizacao; 